import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight, MessageCircle } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { useSite } from "@/hooks/content";
import { SERVICES } from "@/data/services";
import Icon from "@/components/Icon";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const { pathname } = useLocation();
  const { site, waLink } = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileServices(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isActive = (path) => (path === "/" ? pathname === "/" : pathname.startsWith(path));

  return (
    <header
      data-testid="nav-main-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-[0_4px_30px_rgba(3,56,106,0.08)]"
          : "bg-white/70 backdrop-blur-md"
      } border-b border-slate-200/60`}
    >
      <div className="container-sky flex h-[84px] items-center justify-between lg:h-[104px]">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
          <img src={site.logo} alt={`${site.name} logo`} className="h-14 w-auto lg:h-20" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) =>
            link.hasDropdown ? (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-1 rounded-full px-4 py-2 text-[15px] font-medium transition-colors ${
                    isActive(link.path) ? "text-brand" : "text-navy hover:text-brand"
                  }`}
                >
                  {link.name}
                  <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
                </Link>
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute left-1/2 top-full w-[560px] -translate-x-1/2 pt-3"
                    >
                      <div className="grid grid-cols-2 gap-1.5 rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_24px_60px_rgba(3,56,106,0.16)]">
                        {SERVICES.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-surface"
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand/10 to-brand-light/10 text-brand">
                              <Icon name={s.icon} className="h-5 w-5" />
                            </span>
                            <span>
                              <span className="block text-sm font-semibold text-navy group-hover:text-brand-ink">
                                {s.shortTitle}
                              </span>
                              <span className="mt-0.5 block font-mono-tech text-[10px] uppercase tracking-widest text-slate-400">
                                {s.id}
                              </span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-[15px] font-medium transition-colors ${
                  isActive(link.path) ? "text-brand" : "text-navy hover:text-brand"
                }`}
              >
                {link.name}
              </Link>
            )
          )}
        </nav>

        {/* Right CTAs */}
        <div className="flex items-center gap-2.5">
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-btn"
            aria-label="WhatsApp us"
            className="hidden h-11 w-11 items-center justify-center rounded-full border border-[#25D366]/30 text-[#25D366] transition-all hover:bg-[#25D366] hover:text-white sm:flex"
          >
            <MessageCircle className="h-5 w-5" />
          </a>
          <Link
            to="/contact"
            data-testid="cta-request-quote-header"
            className="btn-primary-sky hidden !px-6 !py-3 sm:inline-flex"
          >
            Request a Quote <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-navy lg:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
          >
            <div className="container-sky max-h-[calc(100vh-72px)] overflow-y-auto py-5">
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((link) =>
                  link.hasDropdown ? (
                    <div key={link.name}>
                      <button
                        onClick={() => setMobileServices((v) => !v)}
                        className="flex w-full items-center justify-between rounded-xl px-4 py-3.5 text-left text-base font-semibold text-navy"
                      >
                        {link.name}
                        <ChevronDown className={`h-5 w-5 transition-transform ${mobileServices ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileServices && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden pl-3"
                          >
                            {SERVICES.map((s) => (
                              <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-slate-600"
                              >
                                <Icon name={s.icon} className="h-4 w-4 text-brand" />
                                {s.shortTitle}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.path}
                      className={`rounded-xl px-4 py-3.5 text-base font-semibold ${
                        isActive(link.path) ? "text-brand" : "text-navy"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </nav>
              <div className="mt-4 flex flex-col gap-3">
                <Link to="/contact" className="btn-primary-sky w-full">
                  Request a Quote <ArrowUpRight className="h-4 w-4" />
                </Link>
                <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-whatsapp w-full">
                  <MessageCircle className="h-4 w-4" /> WhatsApp Us
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
